import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { chain } from 'lodash';
import { ROLES } from '@/shared/constants/roles';
import type { EnvironementData, EnvironmentWorkspace } from '@/shared/DTO/GED';

export const useEnvironmentStore = defineStore('environments', () => {
    const environments = ref<EnvironementData[]>([]);
    const currentEnvironment = ref<EnvironementData>({} as EnvironementData);
    const workspaces = ref<EnvironmentWorkspace[]>([]);

    const getEnvironmentById = computed(() => (id: number) => {
        return environments.value.find((environment) => environment.id === id);
    });

    const getUserByActorId = computed(() => (actorId: number) => {
        return currentEnvironment.value.userEnvironments.find((user) => user.actorId === actorId);
    });

    const getWorkspaces = computed(() => {
        if (!workspaces.value) {
            return [];
        }
        return workspaces.value.filter((workspace) => !workspace.group);
    });

    const getWorkspacesInGroup = computed(() => {
        if (!workspaces.value) {
            return [];
        }
        return workspaces.value.filter((workspace) => workspace.group);
    });

    const getAllWorkspaces = computed(() => {
        if (!workspaces.value) {
            return [];
        }
        return workspaces.value;
    });

    const getWorkspacesInGroupById = computed(() => (id: number) => {
        if (!getWorkspacesInGroup.value) {
            return [];
        }
        return getWorkspacesInGroup.value.filter((workspace) => workspace.group?.id === id);
    });

    const getWorkspaceGroups = computed(() => {
        const workspaceGroups = workspaces.value?.filter((workspace) => workspace.group);
        if (!workspaceGroups) {
            return null;
        }

        return chain(workspaceGroups)
            .groupBy((item) => (item.group ? item.group.name : 'No Group'))
            .map((items, groupName) => {
                const group = items.find((item) => item.group && item.group.name === groupName)?.group;
                return {
                    id: group ? group.id : 0,
                    name: groupName,
                    image: group ? group.image : null,
                };
            })
            .value()
            .sort((a, b) => a.name.localeCompare(b.name));
    });

    const getWorkspaceGroupById = computed(() => (id: number) => {
        if (!getWorkspaceGroups.value) {
            return null;
        }
        return getWorkspaceGroups.value.find((group) => group.id === id);
    });

    const getUsersAdmins = computed(() => {
        return currentEnvironment.value.userEnvironments?.filter(
            (user) => user.role === ROLES.ADMIN || user.role === ROLES.SUPER_ADMIN,
        );
    });

    function setEnvironments(envs: EnvironementData[]) {
        environments.value = envs;
    }

    function clear() {
        environments.value = [] as EnvironementData[];
        currentEnvironment.value = {} as EnvironementData;
    }

    return {
        environments,
        currentEnvironment,
        workspaces,
        getEnvironmentById,
        getUserByActorId,
        getWorkspaces,
        getWorkspacesInGroup,
        getAllWorkspaces,
        getWorkspacesInGroupById,
        getWorkspaceGroups,
        getWorkspaceGroupById,
        getUsersAdmins,
        setEnvironments,
        clear,
    };
}, {
    persist: true,
});

export type EnvironmentsStore = ReturnType<typeof useEnvironmentStore>;
